const STORAGE_KEY = 'customer_onboarding_context_v1'
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/
const COUNTER_PUBLIC_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/
const ACTIVE_STATUSES = new Set(['pending', 'deferred'])
const SUCCESS_STATUSES = new Set(['authorized', 'already_authorized'])
const TERMINAL_STATUSES = new Set([
  ...SUCCESS_STATUSES,
  'expired',
  'cancelled',
  'conflict',
  'revoked'
])
const KNOWN_STATUSES = new Set([...ACTIVE_STATUSES, ...TERMINAL_STATUSES])
const KNOWN_KINDS = new Set(['store_join', 'membership_invitation', 'counter_join'])
const SENSITIVE_QUERY_KEY = /^(access_token|api_token|authorization|callback|code|context_token|customer_id|email|id_token|idempotency_key|identifier|jwt|phone|redirect|redirect_uri|return_url|sso_token|state|token)$/i
let capturedFragment = null

export class InvalidOnboardingContextError extends Error {
  constructor(message = 'The onboarding context is invalid.') {
    super(message)
    this.name = 'InvalidOnboardingContextError'
  }
}

export function consumeFragmentToken({ location = window.location, history = window.history } = {}) {
  const rawHash = String(location.hash || '').replace(/^#/, '')
  const params = new URLSearchParams(rawHash)
  const values = params.getAll('token')
  const token = values.length === 1 ? values[0] : ''

  // Scrub the full fragment before any asynchronous work. The raw capability
  // remains in memory only for the lifetime of the current page.
  scrubOnboardingFragment({ location, history })

  return TOKEN_PATTERN.test(token) ? token : null
}

export function scrubOnboardingFragment({ location = window.location, history = window.history } = {}) {
  history.replaceState(history.state, '', safeOnboardingPath(location))
}

export function safeOnboardingPath(location = window.location) {
  const params = new URLSearchParams(String(location.search || '').replace(/^\?/, ''))
  for (const key of [...params.keys()]) {
    if (SENSITIVE_QUERY_KEY.test(key)) params.delete(key)
  }
  const query = params.toString()
  return `${location.pathname}${query ? `?${query}` : ''}`
}

export function captureOnboardingFragment(kind, options) {
  const hadFragment = Boolean((options?.location || window.location).hash)
  capturedFragment = { kind, hadFragment, token: consumeFragmentToken(options) }
  return capturedFragment.token
}

export function takeCapturedOnboardingFragment(kind) {
  if (!capturedFragment || capturedFragment.kind !== kind) return null
  const result = capturedFragment
  capturedFragment = null
  return result
}

export function isCanonicalCounterPublicId(value) {
  return COUNTER_PUBLIC_ID_PATTERN.test(String(value || ''))
}

export function validateOnboardingContext(data, expectedKind, expectedContextId = null) {
  if (!data || Number(data.contract_version) !== 1) {
    throw new InvalidOnboardingContextError('Unsupported onboarding contract.')
  }

  if (!KNOWN_KINDS.has(data.kind) || (expectedKind && data.kind !== expectedKind)) {
    throw new InvalidOnboardingContextError('The onboarding audience does not match.')
  }

  const status = normalizeOnboardingStatus(data.status, data.kind)
  if (!status) {
    throw new InvalidOnboardingContextError('Unknown onboarding status.')
  }

  if (!data.context_id || !data.context_token || !data.expires_at) {
    throw new InvalidOnboardingContextError()
  }

  if (data.kind === 'counter_join' && (
    !isCanonicalCounterPublicId(data.context_id) ||
    (expectedContextId && data.context_id !== expectedContextId)
  )) {
    throw new InvalidOnboardingContextError('The counter QR context does not match this route.')
  }

  if (!data.store || typeof data.store.name !== 'string' || !data.store.name.trim()) {
    throw new InvalidOnboardingContextError('Store branding is unavailable.')
  }

  return {
    contractVersion: 1,
    contextId: String(data.context_id),
    contextToken: String(data.context_token),
    kind: data.kind,
    status,
    expiresAt: data.expires_at,
    store: {
      name: data.store.name.trim(),
      logoUrl: safeStoreLogoUrl(data.store.logo_url)
    },
    authorization: {
      title: typeof data.authorization?.title === 'string' ? data.authorization.title : '',
      effect: typeof data.authorization?.effect === 'string' ? data.authorization.effect : ''
    }
  }
}

export function normalizeOnboardingStatus(status, kind) {
  const serverStatus = String(status || '')
  const mappings = kind === 'counter_join'
    ? {
        active: 'pending'
      }
    : kind === 'store_join'
    ? {
        pending: 'pending',
        deferred: 'deferred',
        completed: 'authorized',
        expired: 'expired',
        cancelled: 'cancelled'
      }
    : kind === 'membership_invitation'
      ? {
          queued: 'pending',
          sent: 'pending',
          deferred: 'deferred',
          accepted: 'authorized',
          superseded: 'cancelled',
          cancelled: 'cancelled',
          expired: 'expired',
          conflict: 'conflict'
        }
      : null

  const normalized = mappings?.[serverStatus]
  return normalized && KNOWN_STATUSES.has(normalized) ? normalized : null
}

export function safeStoreLogoUrl(value, configuredHosts = import.meta.env.VITE_ONBOARDING_LOGO_HOSTS) {
  if (!value) return null

  try {
    const url = new URL(value)
    const allowedHosts = new Set(
      String(configuredHosts || '')
        .split(',')
        .map((host) => host.trim().toLowerCase())
        .filter(Boolean)
    )

    if (window.location.protocol === 'https:') allowedHosts.add(window.location.hostname.toLowerCase())
    if (
      url.protocol !== 'https:' ||
      url.username ||
      url.password ||
      (url.port && url.port !== '443') ||
      !allowedHosts.has(url.hostname.toLowerCase())
    ) return null
    return url.href
  } catch {
    return null
  }
}

export function saveOnboardingContext(context, storage = window.sessionStorage) {
  const payload = {
    contract_version: 1,
    context_id: context.contextId,
    context_token: context.contextToken,
    kind: context.kind,
    expires_at: context.expiresAt,
    authorize_key: context.authorizeKey || null
  }
  if (context.kind !== 'counter_join') payload.defer_key = context.deferKey || null
  storage.setItem(STORAGE_KEY, JSON.stringify(payload))
}

export function loadOnboardingContext(expectedKind, storage = window.sessionStorage, { includeExpired = false } = {}) {
  try {
    const payload = JSON.parse(storage.getItem(STORAGE_KEY))
    if (
      Number(payload?.contract_version) !== 1 ||
      payload.kind !== expectedKind ||
      !payload.context_id ||
      !payload.context_token ||
      !payload.expires_at
    ) return null

    const expiresAt = Date.parse(payload.expires_at)
    if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      clearOnboardingContext(storage)
      return includeExpired ? { expired: true, kind: expectedKind } : null
    }

    if (expectedKind === 'counter_join' && !isCanonicalCounterPublicId(payload.context_id)) {
      clearOnboardingContext(storage)
      return null
    }

    return {
      contextId: payload.context_id,
      contextToken: payload.context_token,
      kind: payload.kind,
      expiresAt: payload.expires_at,
      authorizeKey: payload.authorize_key || null,
      deferKey: payload.defer_key || null
    }
  } catch {
    clearOnboardingContext(storage)
    return null
  }
}

export function clearOnboardingContext(storage = window.sessionStorage) {
  storage.removeItem(STORAGE_KEY)
}

export function actionIdempotencyKey(context, action) {
  if (context.kind === 'counter_join' && action !== 'authorize') {
    throw new Error('Counter QR supports authorization only.')
  }
  const property = action === 'defer' ? 'deferKey' : 'authorizeKey'
  if (!context[property]) context[property] = createIdempotencyKey()
  saveOnboardingContext(context)
  return context[property]
}

export function createIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  if (!window.crypto?.getRandomValues) throw new Error('Secure randomness is unavailable.')
  const bytes = new Uint8Array(16)
  window.crypto.getRandomValues(bytes)
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
}

export function isActiveOnboardingStatus(status) {
  return ACTIVE_STATUSES.has(status)
}

export function isSuccessfulOnboardingStatus(status) {
  return SUCCESS_STATUSES.has(status)
}

export function isTerminalOnboardingStatus(status) {
  return TERMINAL_STATUSES.has(status)
}
